import { Typography, Box, Divider, Alert, Button } from "@mui/material";
import {
    CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../components/common/custom-component/ToastProvider";

import { useSearchParams } from "react-router-dom";

import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import DataContainer, { TableActionButton, TableActionGroup } from "../components/common/DataContainer";
import { useSearchFilter } from "../hooks/useSearchFilter";
import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import SearchFilterBar from "../components/common/SearchFilterBar";
import CustomPagination from "../components/common/CustomPagination";
import { logger } from "helpers/logger";

import { manageContactsReducer, initialState } from "../reducers/manageContactsReducer";
import { useTableColumns } from "../hooks/useTableColumns";
import {
    addTransporterGeneratorContactThunk,
    deleteTransporterGeneratorContactThunk,
    editTransporterGeneratorContactThunk,
    fetchTransporterGeneratorContactsThunk,
    fetchTransporterGeneratorThunk,
} from "store/thunks/mdsThunks";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function ManageContactsPage({ filteredCompanyId: selectedCompanyIdProp } = {}) {
    const dispatch = useDispatch();
    const [t] = useTranslation();
    const [searchParams] = useSearchParams();
     const { success: ToastSuccess, error: ToastError} = useToast();

    const selectedCompanyIdQuery = searchParams.get("filteredCompanyId");
    const companyNameQuery = searchParams.get("companyName");

    const filteredCompanyId = useMemo(() => {
        const v = selectedCompanyIdProp ?? selectedCompanyIdQuery;
        if (v === null || v === undefined || v === "") return undefined;
        return `${v}`;
    }, [selectedCompanyIdProp, selectedCompanyIdQuery]);

    const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);

    const [state, dispatchState] = useReducer(manageContactsReducer, initialState);

    const rawContacts = useMemo(() => Array.isArray(state?.data?.contacts) ? state?.data?.contacts : state?.data?.contacts?.payload, [state?.data?.contacts]);
    const generatorsList = useMemo(() => Array.isArray(state?.data?.generators) ? state?.data?.generators : state?.data?.generators?.payload, [state?.data?.generators]);

    const displayData = useMemo(() => {
        if (!filteredCompanyId) return rawContacts;
        return (rawContacts || []).filter((c) => `${c?.companyId ?? ""}` === `${filteredCompanyId}`);
    }, [rawContacts, filteredCompanyId]);

    const generatorOptions = useMemo(
        () => [
            { value: "0", label: t("Individual") },
            ...generatorsList?.map((g) => ({
                value: `${g?.id ?? ""}`,
                label: t(g?.companyName ?? ""),
                generator: g,
            })),
        ],
        [generatorsList, t]
    );

    const [retryTrigger, setRetryTrigger] = useState(0);
    const [error, setError] = useState(null);
    const handleRetry = () => {
        setRetryTrigger(prev => prev + 1);
    };

    const errorView = error ? (
        <Box sx={{ p: 4, mx: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Alert severity="error" sx={{ width: "100%", maxWidth: 600 }}>
                {error}
            </Alert>
            <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={handleRetry}>
                Retry
            </Button>
        </Box>
    ) : null;

    // #region FETCH DATA
    useEffect(() => {
        let promiseRef = null;

        const fetchData = async () => {
            // Reset states when retrying
            setError(null);
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

            promiseRef = dispatch(fetchTransporterGeneratorContactsThunk({ companyId: selectedCompany.companyId }));

            const recordResult = await promiseRef;

            if (fetchTransporterGeneratorContactsThunk.fulfilled.match(recordResult)) {
                const records = recordResult?.payload?.data;
                dispatchState({ type: "SET_CONTACTS", payload: Array.isArray(records) ? records : [] });
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            } else if (fetchTransporterGeneratorContactsThunk.rejected.match(recordResult)) {
                // Check if it's not a cancellation
                if (recordResult.payload !== 'cancelled') {
                    setError(recordResult.payload || 'An error occurred while loading data');
                    logger.error('An error occurred while loading data');
                }
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            }
        };

        fetchData();

        // Cleanup: abort on unmount - just like saga!
        return () => {
            if (promiseRef) {
                logger.info('🛑 Component unmounting, aborting request');
                promiseRef.abort();
            }
        };
    }, [dispatch, selectedCompany?.companyId, retryTrigger]);
    // #endregion

    // #region SEARCH & PAGINATION
    const { setSearchQuery, filteredData } = useSearchFilter(
        displayData,
        ["firstName", "lastName", "nationalId", "mobileNumber", "address", "email", "generatorCompanyId"],
        null
    );

    const totalPages = Math.max(1, Math.ceil((filteredData?.length || 0) / state.table.recordsPerPage));

    const pagedData = useMemo(() => {
        const start = (state.table.currentPage - 1) * state.table.recordsPerPage;
        const end = start + state.table.recordsPerPage;
        return (filteredData || []).slice(start, end);
    }, [filteredData, state.table.currentPage, state.table.recordsPerPage]);
    // #endregion

    // #region Add CONTACT
    const handleAddClick = useCallback(async () => {
        if (!filteredCompanyId && selectedCompany?.companyId) {
            const records = await dispatch(fetchTransporterGeneratorThunk({ companyId: selectedCompany.companyId }));
            dispatchState({ type: "SET_GENERATORS", payload: Array.isArray(records.payload?.data) ? records.payload?.data : [] });
        }

        if (filteredCompanyId) {
            dispatchState({
                type: "UPDATE_FORM_FIELD",
                payload: { formName: "addContactForm", fieldName: "generatorCompanyId", value: `${filteredCompanyId}` },
            });

            dispatchState({
                type: "UPDATE_FORM_FIELD",
                payload: { formName: "addContactForm", fieldName: "companyId", value: `${filteredCompanyId}` },
            });
        }

        dispatchState({ type: "OPEN_ADD_DIALOG" });
    }, [dispatch, selectedCompany?.companyId, filteredCompanyId]);

    const handleConfirmAdd = useCallback(
        async (formData) => {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

            try {
                const payload = {
                    ...formData,
                    companyId: `${formData.companyId}`,
                    generatorCompanyId: formData.generatorCompanyId,
                };

                const result = await dispatch(
                    addTransporterGeneratorContactThunk({
                        companyId: selectedCompany.companyId,
                        data: payload,
                    })
                );

                if (addTransporterGeneratorContactThunk.rejected.match(result)) {
                    ToastError(t("An error occured, please try again later."));
                    return;
                }

                ToastSuccess(t("Success"));
                const records = await dispatch(fetchTransporterGeneratorContactsThunk({ companyId: selectedCompany.companyId }));
                dispatchState({ type: "SET_CONTACTS", payload: Array.isArray(records?.payload?.data) ? records.payload?.data : [] });
                dispatchState({ type: "CLOSE_ADD_DIALOG" });
            } catch (err) {
                ToastError(t("An error occured, please try again later."));
            } finally {
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            }
        },
        [dispatch, selectedCompany?.companyId, filteredCompanyId, t]
    );

    const handleCancelAdd = useCallback(() => {
        dispatchState({ type: "CLOSE_ADD_DIALOG" });
    }, []);
    // #endregion

    // #region EDIT CONTACT
    const handleEditClick = useCallback(async (row) => {
        const displayCompanyName =
            row?.companyName ??
            row?.generatorCompanyName ??
            row?.generatorCompany ??
            row?.company ??
            `${row?.generatorCompanyId ?? ""}`;

        let editContactInitialFormData = {
            generatorCompanyId: displayCompanyName,
            companyId: `${row?.companyId ?? ""}`,
            firstName: row?.firstName ?? "",
            lastName: row?.lastName ?? "",
            requiredID: row?.requiredID ?? false,
            nationalId: row?.nationalId ?? "",
            mobileNumber: row?.mobileNumber ?? "",
            address: row?.address ?? "",
            email: row?.email ?? "",
            type: row?.type ?? "",
            other: row?.other ?? "",
        };

        dispatchState({
            type: "OPEN_EDIT_DIALOG",
            payload: {
                record: row,
                editFormData: editContactInitialFormData,
                initialEditContactForm: editContactInitialFormData,
            },
        });
    }, []);

    const handleCancelEdit = useCallback(async () => {
        dispatchState({ type: "CLOSE_EDIT_DIALOG" });
    }, []);

    const handleConfirmEdit = useCallback(
        async (formData) => {

            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

            try {
                const payload = {
                    ...formData,
                    companyId: `${formData.companyId}`,
                    generatorCompanyId: formData.generatorCompanyId,
                };

                const result = await dispatch(
                    editTransporterGeneratorContactThunk({
                        contactId: state.dialogs.selectedRecord.id,
                        data: payload,
                    })
                );

                if (editTransporterGeneratorContactThunk.rejected.match(result)) {
                    ToastError(t("An error occured, please try again later."));
                    return;
                }

                ToastSuccess(t("Success"));
                const records = await dispatch(fetchTransporterGeneratorContactsThunk({ companyId: selectedCompany.companyId }));
                dispatchState({ type: "SET_CONTACTS", payload: Array.isArray(records.payload?.data) ? records.payload?.data : [] });
                dispatchState({ type: "CLOSE_EDIT_DIALOG" });
            } catch (err) {
                ToastError(t("An error occured, please try again later."));
            } finally {
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            }
        },
        [dispatch, selectedCompany?.companyId, state.dialogs.selectedRecord, t]
    );
    // #endregion

    // #region VIEW CONTACT
    const handleViewClick = useCallback((row) => {
        const displayCompanyName =
            row?.companyName ??
            row?.generatorCompanyName ??
            row?.generatorCompany ??
            row?.company ??
            `${row?.generatorCompanyId ?? ""}`;

        const viewContactInitialFormData = {
            generatorCompanyId: `${displayCompanyName ?? ""}`,
            companyId: row?.companyId ?? "",
            firstName: row?.firstName ?? "",
            lastName: row?.lastName ?? "",
            requiredID: row?.requiredID ?? false,
            nationalId: row?.nationalId ?? "",
            mobileNumber: row?.mobileNumber ?? "",
            address: row?.address ?? "",
            email: row?.email ?? "",
            type: row?.type ?? "",
            other: row?.other ?? "",
        };

        dispatchState({
            type: "OPEN_VIEW_DIALOG",
            payload: {
                record: row,
                viewFormData: viewContactInitialFormData,
            },
        });
    }, []);

    const handleCancelView = useCallback(() => {
        dispatchState({ type: "CLOSE_VIEW_DIALOG" });
    }, []);
    // #endregion

    // #region DELETE CONTACT
    const handleDeleteClick = useCallback((row) => {
        dispatchState({
            type: "OPEN_DELETE_DIALOG",
            payload: { ...row },
        });
    }, []);

    const handleCancelDelete = useCallback(() => {
        dispatchState({ type: "CLOSE_DELETE_DIALOG" });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!state.dialogs.selectedRecord?.id) {
            dispatchState({ type: "CLOSE_DELETE_DIALOG" });
            return;
        }

        dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

        try {
            const result = await dispatch(deleteTransporterGeneratorContactThunk({ contactId: state.dialogs.selectedRecord.id }));

            if (deleteTransporterGeneratorContactThunk.rejected.match(result)) {
                ToastError(t("An error occured, please try again later."));
                return;
            }

            ToastSuccess(t("Deleted"));
            const records = await dispatch(fetchTransporterGeneratorContactsThunk({ companyId: selectedCompany.companyId }));
            dispatchState({ type: "SET_CONTACTS", payload: Array.isArray(records.payload?.data) ? records.payload?.data : [] });
            dispatchState({ type: "CLOSE_DELETE_DIALOG" });
        } catch (err) {
            ToastError(t("An error occured, please try again later."));
        } finally {
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
        }
    }, [dispatch, selectedCompany?.companyId, state.dialogs.selectedRecord, t]);
    // #endregion

    // #region MANAGE CONTACTS TABLE COLUMNS
    const actions = useMemo(() => ({
        onView: handleViewClick,
        onEdit: handleEditClick,
        onDelete: handleDeleteClick,
    }), [handleViewClick, handleEditClick, handleDeleteClick]);

    const { columns } = useTableColumns('ManageContacts', { actions });
    // #endregion

    // #region RETURN COMPONENT
    if (error) return errorView;

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h3" gutterBottom color="primary">
                {filteredCompanyId
                    ? `${t("Manage Contacts")} - ${t(companyNameQuery || "")}`
                    : t("Manage Contacts")}
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
                {filteredCompanyId
                    ? t("This page shows delegates for the selected generator.")
                    : t("This is a Manage Contacts page.")}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {!displayData || displayData.length === 0 ? (
                <DataContainer
                    data={[]}
                    columns={columns}
                    emptyStateIcon={CalendarIcon}
                    emptyStateTitle={t("No contacts found")}
                    emptyStateSubtitle={t("Start adding delegates to your company")}
                />
            ) : (
                <>
                    <Box sx={{ p: 2 }}>
                        <Box sx={{ my: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                            <TableActionGroup sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                <TableActionButton text={t("Add New Delegate")} variant="contained" onClick={handleAddClick} />
                            </TableActionGroup>

                            <SearchFilterBar
                                mode="frontend"
                                searchValue={state.table.searchInput}
                                onSearchChange={(v) => {
                                    dispatchState({ type: "SET_SEARCH_INPUT", payload: v });
                                    setSearchQuery(v);
                                }}
                                showFilterButton={false}
                                showSearchButton={false}
                                searchPlaceholder={t("Search contacts...")}
                            />
                        </Box>

                        <DataContainer data={pagedData} columns={columns} showRowNumbers maxHeight={600} stickyHeader />
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <CustomPagination
                            currentPage={state.table.currentPage}
                            totalPages={totalPages}
                            onPageChange={(e, v) => dispatchState({ type: "SET_CURRENT_PAGE", payload: v })}
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </>
            )}

            {/* DELETE */}
            <EnhancedConfirmationDialog
                open={state.dialogs.deleteConfirm}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={t("Delete Contact")}
                message={t("Are you sure you want to delete this contact?")}
                confirmText={t("Confirm")}
                cancelText={t("Cancel")}
                confirmColor="error"
                showMessage={true}
            />

            {/* EDIT */}
            <EnhancedConfirmationDialog
                open={state.dialogs.editConfirm}
                onClose={handleCancelEdit}
                onConfirm={handleConfirmEdit}
                title={t("Edit Contact")}
                confirmText={t("Confirm")}
                cancelText={t("Cancel")}
                confirmColor="primary"
                showMessage={false}
                returnChangedFieldsOnly={false}
                defaultValues={state.forms.editContactForm}
                fields={[
                    { name: "generatorCompanyId", label: "Company", type: "text", required: true, disabled: true },
                    { name: "firstName", label: "First Name", type: "text", required: true },
                    { name: "lastName", label: "Last Name", type: "text", required: true },
                    {
                        name: "requiredID",
                        label: "IdNumberAvailable",
                        type: "dropdown",
                        options: state.ui.requiredIdOptions,
                        onChange: (value) =>
                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "editContactForm", fieldName: "requiredID", value },
                            }),
                    },
                    {
                        name: "nationalId",
                        label: "National ID/Iqama ID",
                        type: "text",
                        placeholder: "Enter National ID (10 digits)",
                        regex: /^\d{10}$/,
                        regexErrorMessage: "National ID must be exactly 10 characters long.",
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        numericOnly: true,
                        disabled: !Boolean(state.forms.editContactForm?.requiredID),
                    },
                    {
                        name: "mobileNumber",
                        label: "Mobile Number",
                        type: "text",
                        required: true,
                        placeholder: "Enter mobile number (e.g. 0512345678)",
                        regex: /^05\d{8}$/,
                        regexErrorMessage: "Mobile Number must be 10 digits and start with '05'.",
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        numericOnly: true,
                    },
                    { name: "address", label: "Address", type: "text" },
                    {
                        name: "email",
                        label: "Email",
                        type: "text",
                        placeholder: "Enter email",
                        disabled: `${state.dialogs.selectedRecord?.generatorCompanyId ?? ""}` === "0",
                    },
                ]}
            />

            {/* VIEW */}
            <EnhancedConfirmationDialog
                open={state.dialogs.viewConfirm}
                onClose={handleCancelView}
                onConfirm={handleCancelView}
                title={t("View Contact")}
                confirmText={t("Close")}
                cancelText={t("Close")}
                showMessage={false}
                defaultValues={state.forms.viewContactForm}
                fields={[
                    { name: "generatorCompanyId", label: "Company", type: "text", disabled: true },
                    { name: "firstName", label: "First Name", type: "text", disabled: true },
                    { name: "lastName", label: "Last Name", type: "text", disabled: true },
                    { name: "requiredID", label: "IdNumberAvailable", type: "dropdown", options: state.ui.requiredIdOptions, disabled: true },
                    { name: "nationalId", label: "National ID/Iqama ID", type: "text", disabled: true },
                    { name: "mobileNumber", label: "Mobile Number", type: "text", disabled: true },
                    { name: "address", label: "Address", type: "text", disabled: true },
                    { name: "email", label: "Email", type: "text", disabled: true },
                ]}
            />

            {/* ADD */}
            <EnhancedConfirmationDialog
                open={state.dialogs.addConfirm}
                onClose={handleCancelAdd}
                onConfirm={handleConfirmAdd}
                title={t("Add New Delegate")}
                message={
                    <Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Typography component="span" variant="body2" color="text.secondary">
                            {t("Add contact to your company")}
                        </Typography>
                    </Box>
                }
                cancelText={t("Cancel")}
                confirmText={t("Confirm")}
                confirmColor="primary"
                showMessage={true}
                defaultValues={state.forms.addContactForm || initialState.forms.addContactForm}
                fields={[
                    {
                        name: "generatorCompanyId",
                        label: "Company",
                        type: "dropdown",
                        required: true,
                        options: generatorOptions,
                        disabled: Boolean(filteredCompanyId),
                        onChange: (optionValue) => {
                            const selectedOption = generatorOptions.find((o) => `${o.value}` === `${optionValue}`);
                            const selectedGenerator = selectedOption?.generator;

                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addContactForm", fieldName: "generatorCompanyId", value: optionValue },
                            });

                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: {
                                    formName: "addContactForm",
                                    fieldName: "companyId",
                                    value: selectedGenerator?.id ?? "0",
                                },
                            });
                        },
                    },
                    { name: "firstName", label: "First Name", type: "text", required: true },
                    { name: "lastName", label: "Last Name", type: "text", required: true },
                    {
                        name: "requiredID",
                        label: t("IdNumberAvailable"),
                        type: "radio",
                        options: [
                            { value: true, label: t("Yes") },
                            { value: false, label: t("No") },
                        ],
                        onChange: (value) => {
                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addContactForm", fieldName: "requiredID", value },
                            });

                            if (value === false) {
                                dispatchState({
                                    type: "UPDATE_FORM_FIELD",
                                    payload: { formName: "addContactForm", fieldName: "nationalId", value: "" },
                                });
                            }
                        },
                    },
                    {
                        name: "nationalId",
                        label: "National ID/Iqama ID",
                        type: "text",
                        placeholder: "Enter National ID (10 digits)",
                        regex: /^\d{10}$/,
                        regexErrorMessage: "National ID must be exactly 10 characters long.",
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        numericOnly: true,
                        disabled: !Boolean(state.forms.addContactForm?.requiredID),
                    },
                    {
                        name: "mobileNumber",
                        label: "Mobile Number",
                        type: "text",
                        required: true,
                        placeholder: "Enter mobile number (e.g. 0512345678)",
                        regex: /^05\d{8}$/,
                        regexErrorMessage: "Mobile Number must be 10 digits and start with '05'.",
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        numericOnly: true,
                    },
                    { name: "address", label: "Address", type: "text" },
                    {
                        name: "email",
                        label: "Email",
                        type: "text",
                        required: `${state.forms.addContactForm?.generatorCompanyId ?? ""}` !== "0",
                        placeholder: "Enter email",
                        disabled: `${state.forms.addContactForm?.generatorCompanyId ?? ""}` === "0",
                        regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        regexErrorMessage: t("Please enter a valid email address."),
                    },
                ]}
            />

            <LoadingBackdrop
                open={state.loading.isLoadingBackdrop}
                message={t("Loading data, please wait...")}
                variant="bars"
                color="greenGradient"
            />
        </Box>
    );
    // #endregion
}
